import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { meetingsApi } from '../../../lib/api';
import { useAuth } from '../../../app/providers/AuthProvider';
import {
  Search,
  Plus,
  Video,
  VideoOff,
  Calendar,
  Clock,
  Users,
  MoreVertical,
  Play,
  Square,
  Trash2,
  Edit,
  Copy,
  ExternalLink,
  Loader2,
  RefreshCw,
  Circle,
  Zap,
} from 'lucide-react';

interface Meeting {
  id: string;
  title: string;
  description: string | null;
  room_name: string;
  meeting_type: 'lesson' | 'meeting' | 'event' | 'office_hours';
  scheduled_start: string;
  scheduled_end: string | null;
  actual_start: string | null;
  actual_end: string | null;
  status: 'scheduled' | 'live' | 'ended';
  recording_enabled: boolean;
  auto_recording: boolean;
  recording_status: string | null;
  host: { id: string; name: string } | null;
  course: { id: string; name: string } | null;
  school: { id: string; name: string } | null;
}

interface MeetingFormData {
  title: string;
  description: string;
  meeting_type: Meeting['meeting_type'];
  scheduled_start: string;
  scheduled_end: string;
  recording_enabled: boolean;
  auto_recording: boolean;
  course_id?: string;
}

export function MeetingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentSchool } = useAuth();

  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
  const [contextMenuId, setContextMenuId] = useState<string | null>(null);
  const [startingInstant, setStartingInstant] = useState(false);

  // Fetch meetings
  const fetchMeetings = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.meeting_type = typeFilter;

      const response = await meetingsApi.list(params);
      setMeetings(response.meetings);
    } catch (err: any) {
      console.error('Failed to fetch meetings:', err);
      setError('Failed to load meetings');
      // Fallback mock data
      setMeetings([
        {
          id: '1',
          title: 'Mathematics Class - Grade 10',
          description: 'Weekly math class',
          room_name: 'room-math-101',
          meeting_type: 'lesson',
          scheduled_start: new Date(Date.now() + 3600000).toISOString(),
          scheduled_end: new Date(Date.now() + 7200000).toISOString(),
          actual_start: null,
          actual_end: null,
          status: 'scheduled',
          recording_enabled: true,
          auto_recording: true,
          recording_status: null,
          host: { id: '1', name: 'Sarah Cohen' },
          course: { id: '1', name: 'Mathematics Advanced' },
          school: { id: '1', name: 'VIScool School' },
        },
        {
          id: '2',
          title: 'Staff Meeting',
          description: 'Monthly staff meeting',
          room_name: 'room-staff-001',
          meeting_type: 'meeting',
          scheduled_start: new Date().toISOString(),
          scheduled_end: new Date(Date.now() + 3600000).toISOString(),
          actual_start: new Date().toISOString(),
          actual_end: null,
          status: 'live',
          recording_enabled: false,
          auto_recording: false,
          recording_status: null,
          host: { id: '2', name: 'Admin User' },
          course: null,
          school: { id: '1', name: 'VIScool School' },
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, [statusFilter, typeFilter]);

  // Filter meetings locally for search
  const filteredMeetings = meetings.filter((meeting) => {
    const matchesSearch =
      meeting.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      meeting.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      meeting.course?.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Group meetings by status
  const liveMeetings = filteredMeetings.filter(m => m.status === 'live');
  const upcomingMeetings = filteredMeetings.filter(m => m.status === 'scheduled');
  const pastMeetings = filteredMeetings.filter(m => m.status === 'ended');

  const handleJoinMeeting = async (meetingId: string) => {
    navigate(`/admin/meetings/${meetingId}`);
  };

  const handleStartMeeting = async (meetingId: string) => {
    try {
      await meetingsApi.start(meetingId);
      fetchMeetings();
    } catch (err) {
      console.error('Failed to start meeting:', err);
    }
  };

  const handleEndMeeting = async (meetingId: string) => {
    try {
      await meetingsApi.end(meetingId);
      fetchMeetings();
    } catch (err) {
      console.error('Failed to end meeting:', err);
    }
  };

  const handleDeleteMeeting = async (meetingId: string) => {
    if (!confirm('Are you sure you want to delete this meeting?')) return;
    try {
      await meetingsApi.delete(meetingId);
      fetchMeetings();
    } catch (err) {
      console.error('Failed to delete meeting:', err);
    }
    setContextMenuId(null);
  };

  const handleCopyLink = (meetingId: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/admin/meetings/${meetingId}`);
    setContextMenuId(null);
  };

  // Start an instant meeting
  const handleInstantMeeting = async () => {
    if (!currentSchool?.id) {
      setError('No school selected');
      return;
    }

    setStartingInstant(true);
    setError(null);

    try {
      const now = new Date();
      const endTime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now

      // Create the meeting
      const response = await meetingsApi.create({
        school_id: currentSchool.id,
        title: `Instant Meeting - ${now.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}`,
        description: 'Instant meeting started from meetings page',
        meeting_type: 'meeting',
        scheduled_start: now.toISOString(),
        scheduled_end: endTime.toISOString(),
        recording_enabled: false,
        auto_recording: false,
      });

      // Start the meeting immediately
      await meetingsApi.start(response.meeting.id);

      // Navigate to the meeting room
      navigate(`/admin/meetings/${response.meeting.id}`);
    } catch (err: any) {
      console.error('Failed to start instant meeting:', err);
      setError(err.message || 'Failed to start instant meeting');
      setStartingInstant(false);
    }
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
    };
  };

  const getStatusBadge = (status: Meeting['status']) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-700',
      live: 'bg-green-100 text-green-700',
      ended: 'bg-gray-100 text-gray-700',
    };
    const labels = {
      scheduled: 'Scheduled',
      live: 'Live',
      ended: 'Ended',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${styles[status]}`}>
        {status === 'live' && <Circle className="w-2 h-2 fill-current animate-pulse" />}
        {labels[status]}
      </span>
    );
  };

  const MeetingCard = ({ meeting }: { meeting: Meeting }) => {
    const { date, time } = formatDateTime(meeting.scheduled_start);
    const endTime = meeting.scheduled_end ? formatDateTime(meeting.scheduled_end).time : '--:--';

    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {getStatusBadge(meeting.status)}
              {meeting.recording_enabled && (
                <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full flex items-center gap-1">
                  <Circle className="w-2 h-2 fill-current" />
                  REC
                </span>
              )}
            </div>

            <h3 className="font-medium text-gray-900 truncate mb-1">{meeting.title}</h3>

            {meeting.course && (
              <p className="text-sm text-teal-600 mb-1">{meeting.course.name}</p>
            )}

            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {date}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {time} - {endTime}
              </span>
              {meeting.host && (
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {meeting.host.name}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {meeting.status === 'scheduled' && (
              <>
                <button
                  onClick={() => handleStartMeeting(meeting.id)}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 flex items-center gap-1"
                >
                  <Play className="w-4 h-4" />
                  Start
                </button>
                <button
                  onClick={() => handleJoinMeeting(meeting.id)}
                  className="px-3 py-1.5 text-sm font-medium text-teal-600 bg-teal-50 rounded-lg hover:bg-teal-100"
                >
                  Preview
                </button>
              </>
            )}

            {meeting.status === 'live' && (
              <>
                <button
                  onClick={() => handleJoinMeeting(meeting.id)}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center gap-1"
                >
                  <Video className="w-4 h-4" />
                  Join
                </button>
                <button
                  onClick={() => handleEndMeeting(meeting.id)}
                  className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 flex items-center gap-1"
                >
                  <Square className="w-4 h-4" />
                  End
                </button>
              </>
            )}

            <div className="relative">
              <button
                onClick={() => setContextMenuId(contextMenuId === meeting.id ? null : meeting.id)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <MoreVertical className="w-4 h-4" />
              </button>

              {contextMenuId === meeting.id && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  <button
                    onClick={() => {
                      setEditingMeeting(meeting);
                      setShowCreateModal(true);
                      setContextMenuId(null);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Edit className="w-4 h-4" />
                    Edit Meeting
                  </button>
                  <button
                    onClick={() => handleCopyLink(meeting.id)}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Copy className="w-4 h-4" />
                    Copy Link
                  </button>
                  <button
                    onClick={() => {
                      window.open(`/meeting/${meeting.id}`, '_blank');
                      setContextMenuId(null);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open in New Tab
                  </button>
                  {meeting.status !== 'live' && (
                    <>
                      <hr className="my-1" />
                      <button
                        onClick={() => handleDeleteMeeting(meeting.id)}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search meetings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="all">All Status</option>
          <option value="scheduled">Scheduled</option>
          <option value="live">Live</option>
          <option value="ended">Ended</option>
        </select>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="all">All Types</option>
          <option value="lesson">Lessons</option>
          <option value="meeting">Meetings</option>
          <option value="event">Events</option>
          <option value="office_hours">Office Hours</option>
        </select>

        <button
          onClick={fetchMeetings}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          <RefreshCw className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={handleInstantMeeting}
            disabled={startingInstant}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            {startingInstant ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Zap className="w-4 h-4" />
            )}
            Instant Meeting
          </button>

          <button
            onClick={() => {
              setEditingMeeting(null);
              setShowCreateModal(true);
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Schedule Meeting
          </button>
        </div>
      </div>

      {/* Live Meetings */}
      {liveMeetings.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Circle className="w-3 h-3 fill-green-500 text-green-500 animate-pulse" />
            Live Now ({liveMeetings.length})
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {liveMeetings.map((meeting) => (
              <MeetingCard key={meeting.id} meeting={meeting} />
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Meetings */}
      {upcomingMeetings.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Upcoming ({upcomingMeetings.length})
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {upcomingMeetings.map((meeting) => (
              <MeetingCard key={meeting.id} meeting={meeting} />
            ))}
          </div>
        </div>
      )}

      {/* Past Meetings */}
      {pastMeetings.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Past ({pastMeetings.length})
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {pastMeetings.map((meeting) => (
              <MeetingCard key={meeting.id} meeting={meeting} />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredMeetings.length === 0 && (
        <div className="text-center py-12">
          <VideoOff className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-4">No meetings found</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 text-sm font-medium text-teal-600 bg-teal-50 rounded-lg hover:bg-teal-100"
          >
            Create your first meeting
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <MeetingFormModal
          meeting={editingMeeting}
          schoolId={currentSchool?.id || ''}
          onClose={() => {
            setShowCreateModal(false);
            setEditingMeeting(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setEditingMeeting(null);
            fetchMeetings();
          }}
        />
      )}
    </div>
  );
}

// Meeting Form Modal Component
interface MeetingFormModalProps {
  meeting: Meeting | null;
  schoolId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function MeetingFormModal({ meeting, schoolId, onClose, onSuccess }: MeetingFormModalProps) {
  // Helper to format date for datetime-local input (local timezone)
  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState<MeetingFormData>({
    title: meeting?.title || '',
    description: meeting?.description || '',
    meeting_type: meeting?.meeting_type || 'meeting',
    scheduled_start: meeting?.scheduled_start
      ? formatDateTimeLocal(new Date(meeting.scheduled_start))
      : formatDateTimeLocal(new Date(Date.now() + 3600000)),
    scheduled_end: meeting?.scheduled_end
      ? formatDateTimeLocal(new Date(meeting.scheduled_end))
      : formatDateTimeLocal(new Date(Date.now() + 7200000)),
    recording_enabled: meeting?.recording_enabled ?? false,
    auto_recording: meeting?.auto_recording ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Convert datetime-local values to ISO strings with timezone
      const payload = {
        ...formData,
        scheduled_start: new Date(formData.scheduled_start).toISOString(),
        scheduled_end: new Date(formData.scheduled_end).toISOString(),
      };

      if (meeting) {
        await meetingsApi.update(meeting.id, payload);
      } else {
        await meetingsApi.create({
          ...payload,
          school_id: schoolId,
        });
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to save meeting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {meeting ? 'Edit Meeting' : 'Create New Meeting'}
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meeting Type
              </label>
              <select
                value={formData.meeting_type}
                onChange={(e) => setFormData({ ...formData, meeting_type: e.target.value as Meeting['meeting_type'] })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="meeting">Meeting</option>
                <option value="lesson">Lesson</option>
                <option value="event">Event</option>
                <option value="office_hours">Office Hours</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_start}
                  onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time
                </label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_end}
                  onChange={(e) => setFormData({ ...formData, scheduled_end: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.recording_enabled}
                  onChange={(e) => setFormData({ ...formData, recording_enabled: e.target.checked })}
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700">Enable Recording</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.auto_recording}
                  onChange={(e) => setFormData({ ...formData, auto_recording: e.target.checked })}
                  disabled={!formData.recording_enabled}
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 disabled:opacity-50"
                />
                <span className="text-sm text-gray-700">Auto-start Recording</span>
              </label>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {meeting ? 'Save Changes' : 'Create Meeting'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
